import React, { useEffect, useState } from 'react';
import { apiUrl } from '../lib/config';

const AUTH_KEY = 'miniapp_auth_v1';

type Props = {
    children: React.ReactNode;
};

async function hashForStorage(password: string): Promise<string> {
    const data = new TextEncoder().encode('miniapp_local_' + password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

const LockScreen: React.FC<Props> = ({ children }) => {
    const [state, setState] = useState<'loading' | 'locked' | 'unlocked'>('loading');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [checking, setChecking] = useState(false);

    const tryAuth = async (pwd: string): Promise<boolean> => {
        try {
            const resp = await fetch(apiUrl('/api/auth'), {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ password: pwd }),
            });
            return resp.ok;
        } catch {
            return false;
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await fetch(apiUrl('/api/settings/public'));
                const data = await resp.json() as { ok: boolean; passwordRequired: boolean };
                if (cancelled) return;

                if (!data.passwordRequired) {
                    setState('unlocked');
                    return;
                }

                // Check saved credentials
                const saved = localStorage.getItem(AUTH_KEY);
                if (saved) {
                    try {
                        const { p } = JSON.parse(saved);
                        if (p && await tryAuth(p)) {
                            if (!cancelled) setState('unlocked');
                            return;
                        }
                    } catch { /* corrupted, ignore */ }
                    localStorage.removeItem(AUTH_KEY);
                }

                if (!cancelled) setState('locked');
            } catch {
                if (!cancelled) setState('unlocked');
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const pwd = password.trim();
        if (!pwd) return;

        setChecking(true);
        setError('');

        try {
            if (await tryAuth(pwd)) {
                // Save a local hash (not the plain password) + the password for re-auth
                // We need the original password to re-send to server on reload
                // So we encrypt it lightly with a local hash as a marker
                const localHash = await hashForStorage(pwd);
                localStorage.setItem(AUTH_KEY, JSON.stringify({ h: localHash, p: pwd }));
                setState('unlocked');
            } else {
                setError('Password non corretta');
                setPassword('');
            }
        } catch {
            setError('Errore di connessione. Riprova.');
        } finally {
            setChecking(false);
        }
    };

    if (state === 'loading') {
        return (
            <div className="lock-screen">
                <p>Caricamento...</p>
            </div>
        );
    }

    if (state === 'unlocked') {
        return <>{children}</>;
    }

    return (
        <div className="lock-screen">
            <div className="lock-card">
                <div className="lock-icon">🔒</div>
                <h2>Accesso riservato</h2>
                <p className="muted">Inserisci la password per accedere</p>

                <form onSubmit={handleSubmit} className="lock-form">
                    <input
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Password"
                        autoFocus
                        autoComplete="off"
                    />
                    {error && <p className="field-error">{error}</p>}
                    <button type="submit" disabled={checking || !password.trim()}>
                        {checking ? 'Verifica...' : 'Accedi'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default LockScreen;
